import { i18n } from "./i18n";
import { setDefaultYupTranslations } from "./setDefaultYupTranslations";

const DEFAULT_LANGUAGE_KEY = "en";

export const SUPPORTED_LANGUAGES = [
  {
    key: DEFAULT_LANGUAGE_KEY,
    value: "English",
  },
  {
    key: "sv-SE",
    value: "Svenska (Sverige)",
  },
  {
    key: "de-DE",
    value: "Deutsch (Deutschland)",
  },
  {
    key: "fr-CH",
    value: "Français (Suisse)",
  },
  {
    key: "fr-FR",
    value: "Français (France)",
  },
  {
    key: "fr-BE",
    value: "Français (Belgique)",
  },
  {
    key: "it-CH",
    value: "Italiano (Svizzera)",
  },
  {
    key: "ja-JP",
    value: "日本語 (日本)",
  },
  {
    key: "ko-KR",
    value: "한국어 (대한민국)",
  },
  {
    key: "nb-NO",
    value: "Norsk bokmål (Norge)",
  },
  {
    key: "nl-BE",
    value: "Nederlands (België)",
  },
  {
    key: "pt-PT",
    value: "Português (Portugal)",
  },
  {
    key: "zh-CN",
    value: "中文 (中国)",
  },
] as const;

export const supportedLanguages = Object.values(SUPPORTED_LANGUAGES).map(
  ({ key }) => key,
);

export type SupportedLanguage = (typeof supportedLanguages)[number];

interface LanguageContent {
  readonly [key: string]: string | LanguageContent;
}

/**
 * Lazy loads the language files so
 * they end up in a separate file (chunk)
 * and only one file (chunk) will be loaded
 * depending on the selected language
 */

export const getSupportedLanguage = (value: string): SupportedLanguage => {
  if (!supportedLanguages.includes(value as SupportedLanguage)) {
    return DEFAULT_LANGUAGE_KEY;
  }

  return value as SupportedLanguage;
};

/**
 * Load language bundles from `public/locales` folder, so they can be replaced without re-deployment of the application
 * Function should be used for stand-alone deployment of Workload Planning
 */
export const addLanguageBundlesStandalone = async (
  lang: SupportedLanguage,
): Promise<void> => {
  const resources: LanguageContent = await (
    await fetch(`/locales/${lang}.json?t=${Date.now()}`)
  ).json();
  i18n.addResourceBundle(lang, "common", resources, true);
  await i18n.changeLanguage(lang);
  setDefaultYupTranslations();
};

/**
 * Load language bundles source code, bundled for the OPP parcel.
 * Function should be used when loading Workload Planning as the component in OPP.
 *
 * If statement for each file is used in order for Vite to be able to bundle the files.
 */
export const addLanguageBundlesParcel = async (
  lang: SupportedLanguage,
): Promise<void> => {
  try {
    // Always load English first as the base fallback
    let resources: LanguageContent = await import("../locales/en.json");

    // Try to load the specific language bundle, if it fails we'll use English
    try {
      if (lang === "sv-SE") {
        resources = await import("../locales/sv-SE.json");
      } else if (lang === "de-DE") {
        resources = await import("../locales/de-DE.json");
      } else if (lang === "fr-CH") {
        resources = await import("../locales/fr-CH.json");
      } else if (lang === "fr-FR") {
        resources = await import("../locales/fr-FR.json");
      } else if (lang === "fr-BE") {
        resources = await import("../locales/fr-BE.json");
      } else if (lang === "it-CH") {
        resources = await import("../locales/it-CH.json");
      } else if (lang === "ja-JP") {
        resources = await import("../locales/ja-JP.json");
      } else if (lang === "ko-KR") {
        resources = await import("../locales/ko-KR.json");
      } else if (lang === "nb-NO") {
        resources = await import("../locales/nb-NO.json");
      } else if (lang === "nl-BE") {
        resources = await import("../locales/nl-BE.json");
      } else if (lang === "pt-PT") {
        resources = await import("../locales/pt-PT.json");
      } else if (lang === "zh-CN") {
        resources = await import("../locales/zh-CN.json");
      }
    } catch (importError) {
      console.warn(
        `Failed to load language bundle for ${lang}, using English fallback:`,
        importError,
      );
      // resources already contains English from the initial load above
    }

    i18n.addResourceBundle(lang, "common", resources, true);
    await i18n.changeLanguage(lang);
    setDefaultYupTranslations();
  } catch (error) {
    console.error("Failed to load language bundles:", error);
    // Last resort: try to load English as fallback
    try {
      const fallbackResources: LanguageContent = await import(
        "../locales/en.json"
      );
      i18n.addResourceBundle("en", "common", fallbackResources, true);
      await i18n.changeLanguage("en");
      setDefaultYupTranslations();
    } catch (fallbackError) {
      console.error("Failed to load fallback English bundle:", fallbackError);
      // If even the fallback fails, we'll continue without translations
    }
  }
};
